import React, { useContext, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { TimelineContext } from '../../../../../../contexts/TimelineContext'
import ProjectAsset from '../../../../../../models/ProjectAsset'
import {
  formatTimelineDateForInput,
  isTimelineEndDatePassed,
} from '../../../../../../utils/timelineAssetDates'
import { resolveAdvertiserSidebarNote } from '../../../../../../utils/timelineAssetPlacement'
import AssetListItemTitle from '../../../../../Shared/TimelineParts/AssetListItemTitle'
import AssetListItemNoteIcon from '../../../../../Shared/TimelineParts/AssetListItemNoteIcon'
import AssetListItemInlineDates from '../../../../../Shared/TimelineParts/AssetListItemInlineDates'

const baseRowClassName =
  'mb-2 grid min-h-7 w-full min-w-0 items-center gap-1 rounded-md border-1 px-2 py-1 text-left'
const groupedRowClassName =
  'mb-2 grid min-h-9 w-full min-w-0 items-center gap-1.5 rounded-xl border px-3 py-2.5 text-left'
const baseRowDefaultStateClassName =
  'border-primary bg-white dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode'
const groupedRowDefaultStateClassName =
  'border-rb-widget-border bg-white shadow-sm dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode'
const passedRowStateClassName =
  'border-gray-300 bg-gray-100 opacity-75 shadow-none grayscale dark:border-gray-600 dark:bg-rb-dark-mode-lighter/60'

const baseRowStyle = { gridTemplateColumns: 'minmax(0, 1fr) auto auto' }
const baseRowWithoutDatesStyle = { gridTemplateColumns: 'minmax(0, 1fr) auto' }

function resolveAdvertiserSidebarDates(asset, campaignScenario) {
  if (asset instanceof ProjectAsset) {
    const placement = campaignScenario?.campaign_scenarios_project_assets?.find(
      (c) => +c.project_asset_id === +asset.id,
    )
    return {
      startDate: formatTimelineDateForInput(placement?.start_date || asset?.start_date || asset?.start),
      endDate: formatTimelineDateForInput(placement?.end_date || asset?.end_date || asset?.end),
    }
  }

  return {
    startDate: formatTimelineDateForInput(asset?.start_date),
    endDate: formatTimelineDateForInput(asset?.end_date),
  }
}

export default observer(function AssetListItem({ asset, onClick, grouped = false }) {
  const { campaignScenario, timelineState } = useContext(TimelineContext)
  const showSidebarDates = timelineState.sidebarShowAssetDates

  const note = useMemo(
    () => resolveAdvertiserSidebarNote(asset, campaignScenario),
    [asset, campaignScenario],
  )

  const { startDate, endDate } = useMemo(
    () => resolveAdvertiserSidebarDates(asset, campaignScenario),
    [asset, campaignScenario],
  )

  const isProjectAsset = asset instanceof ProjectAsset
  const isPassed = isTimelineEndDatePassed(endDate)
  const rowBaseClassName = grouped ? groupedRowClassName : baseRowClassName
  const rowStateClassName = isPassed
    ? passedRowStateClassName
    : grouped
      ? groupedRowDefaultStateClassName
      : baseRowDefaultStateClassName
  const rowClassName = `${rowBaseClassName} ${rowStateClassName}`

  const content = (
    <>
      <AssetListItemTitle
        asset={asset}
        variant={isProjectAsset ? 'project' : 'scenario'}
        passed={isPassed}
        showAuthUsername={!isProjectAsset}
        grouped={grouped}
      />

      {showSidebarDates && (
        <AssetListItemInlineDates startDate={startDate} endDate={endDate} readOnly />
      )}

      <AssetListItemNoteIcon note={note} className={grouped ? 'text-rb-lighter-grey' : ''} />
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${rowClassName} cursor-pointer hover:bg-gray-50 dark:hover:bg-rb-dark-mode-lighter`}
        style={showSidebarDates ? baseRowStyle : baseRowWithoutDatesStyle}
      >
        {content}
      </button>
    )
  }

  return (
    <div
      className={`${rowClassName} cursor-default`}
      style={showSidebarDates ? baseRowStyle : baseRowWithoutDatesStyle}
    >
      {content}
    </div>
  )
})
